import { isStaging } from "./utils/utils";

/**
 * Determines the appropriate base URL for the design-stream API
 * based on whether we're running locally, staging, or production
 */
/**
 * Explicit base-URL override (VITE_DS_API_BASE_URL). Lets the nightly e2e job point
 * the app at a deployed backend instead of localhost:8080, and lets a developer run
 * the frontend without ExpressApp. Unset everywhere else, so normal resolution is
 * unchanged. Same read pattern as workspacesEnabled() — see utils/workspace/featureFlag.ts.
 */
const readApiBaseOverride = (): string | undefined => {
    let value: string | undefined;
    try {
        value = (import.meta as any).env?.VITE_DS_API_BASE_URL;
    } catch {
        /* import.meta.env unavailable in this context (e.g. plain Node) */
    }
    if (value === undefined && typeof process !== "undefined") {
        value = process.env?.VITE_DS_API_BASE_URL;
    }
    return value || undefined;
};

/**
 * Build-time signal (VITE_DS_PREVIEW, baked by node.js.yml's `deploy` job) that THIS
 * PR also deployed a design-stream-backend-pr-<N> Cloud Run preview. Without it, a
 * preview channel talks to the staging backend. Mirrors productFinderPreviewDeployed()
 * in apps/creator/src/Home/library/supabaseSearchClient.ts, including reading only
 * process.env under vitest (tests/setup.js scrubs it; tests use vi.stubEnv).
 */
export const designStreamPreviewDeployed = (): boolean => {
    if (typeof process !== "undefined" && process.env?.VITEST) {
        return process.env.VITE_DS_PREVIEW === "true";
    }
    let value: string | undefined;
    try {
        value = (import.meta as any).env?.VITE_DS_PREVIEW;
    } catch {
        /* import.meta.env unavailable in this context (e.g. plain Node) */
    }
    if (value === undefined && typeof process !== "undefined") {
        value = process.env?.VITE_DS_PREVIEW;
    }
    return value === "true";
};

export function getSearchApiBaseUrl(): string {
    const override = readApiBaseOverride();
    if (override) return override;

    const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname.startsWith('192.168.') ||
            window.location.hostname.endsWith('.local'));

    const useStaging = isStaging();

    if (isLocalhost) {
        return 'http://localhost:8080';
    }

    // PR preview: mattoboard-staging--preview-{PR_NUMBER}[-hash].web.app, only when
    // that PR deployed its own backend; otherwise fall through to staging.
    if (typeof window !== 'undefined' && designStreamPreviewDeployed()) {
        const match = window.location.hostname.match(
            /^mattoboard-staging--preview-(\d+)/,
        );
        if (match) {
            const prNumber = match[1];
            return `https://design-stream-backend-pr-${prNumber}-829457126264.us-central1.run.app`;
        }
    }

    if (useStaging) {
        // Stage / Daily
        return 'https://design-stream-backend-829457126264.us-central1.run.app';
    } else {
        // Production / Beta
        return 'https://design-stream-backend-709632310154.us-central1.run.app';
    }
}
